/**
 * Product updates (Phase 86, UI-SPEC S7, D-14 / D-15 / D-22).
 *
 * The worklist of products that still owe platen variant files after a
 * Platen Size rule changed. Rules changes never rewrite products (D-14): a
 * size whose served file already exists keeps printing it until an upload
 * replaces it, and a size with no file raises a print issue. Without this list
 * the only way to find the work would be a print failure on the floor. The server decides "missing" with the same shared coverage test the
 * colour grid and print time use, so a size that fails at print with "This
 * size needs its own platen variant file" is listed here until its file lands.
 *
 * Each row opens a focused view that shows ONLY the missing slots, grouped by
 * platen variant. Every slot is the exported `ColourArtworkCell`, publishing
 * straight to exactly the SKUs that lack the file in ONE group commit, with
 * the same pixel-size check as colour mode (one dialog for the view), and
 * sending the need's platen variant key so the server refuses a SKU whose rule
 * changed since the list loaded. Non-admins see the same slots read-only.
 *
 * The page owns the scan (`useProductUpdatesScan`) so the trigger keeps its
 * count across tab switches. This component owns only presentation state:
 * which row is open and which rows just finished.
 *
 * Deep link (`?product=`, `focusProductId`): a print-file issue for a platen
 * variant size links here with its product. The walk already folds every
 * page, so the tab waits until that product's row is folded, then opens it,
 * scrolls it just under the sticky bar, moves keyboard focus to its toggle
 * (without a second scroll) and tints it once (`useOneShotHighlight`, 1200 ms,
 * DESIGN.md section 5). Rows folded later can land ABOVE it (the list is
 * sorted by title), so it is kept in view until the walk completes or the
 * operator scrolls, types or clicks. If the walk completes without it, a
 * dismissible note says the product has nothing missing. Handled once, then
 * the page clears the param.
 *
 * Store SKUs (Phase 88, D-06, S15, S19). A composition org's standard products
 * get per-size files only once their store SKUs are set up, so the list never
 * implies nothing is missing when standards are simply not set up:
 *   - switch OFF, nothing listed: a neutral "not set up yet" empty state with
 *     a Store SKUs settings link for admins, not the green "Nothing needs
 *     files" (that one stays for the switch ON and for every other org);
 *   - switch ON: products that still need set-up (`setUp.state` `needed`)
 *     are grouped under one heading above the files list, with a link to the
 *     Products list filtered to them (where the Set up button lives), and no
 *     per-row action. Products whose store SKU changed since set-up get their
 *     own group with its own next step (check the store, then ask for help)
 *     and a link to the Products list filtered to changed ones. The header
 *     counts each kind separately;
 *   - a deep link to a product that is not set up, or whose store SKU
 *     changed, says so, never "This product has no missing files";
 *   - set up by uploading (88a.1, D4): a not-set-up product whose ruled
 *     sizes wait for their file (`setUp.waitingForFile` above 0, only while
 *     the server opts in) is finished here. One upload sets the whole
 *     product up, so it sits in the files list with a line saying so (admins
 *     only: a viewer cannot upload), never in the set-up group, and a deep
 *     link opens it on its upload cells;
 *   - the first upload into a store SKU that prints from an existing file
 *     today asks once per product, exactly as the Products tab does (the
 *     acceptance is shared, `lib/replace-notice.ts`).
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowRight, Barcode, CheckCircle2, Frame, Loader2, Lock } from 'lucide-react';
import { DESIGN_SET_ORDER, isPlatenSideCovered, type DesignSet, type PlatenSizeAssignment } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PageLoader } from '@/components/loading/PageLoader';
import { SectionError } from '@/components/shared/SectionError';
import {
  ONE_SHOT_HIGHLIGHT_CLASS,
  ONE_SHOT_HIGHLIGHT_TRANSITION,
  useOneShotHighlight,
} from '@/hooks/useOneShotHighlight';
import { formatPlatenSizeRange, platenGroupLabel } from '@/lib/platen-size-range';
import { BELOW_ACTION_BAR_SCROLL_MARGIN, scrollIntoViewBelowActionBar } from '@/lib/scroll-container';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { ArtworkLocationGate } from './ArtworkLocationGate';
import { ArtworkTargetPicker } from './ArtworkTargetPicker';
import { ColourArtworkCell, type PlatenReplaceNotice } from './ColourArtworkGrid';
import { StudioEntryLink } from './StudioEntryLink';
import type { ArtworkUploadTarget } from '../hooks/useArtworkUploadLocation';
import { expectedPlatenFilePixels, usePlatenPixelCheck } from '../hooks/usePlatenPixelCheck';
import { useOrgPlatenContext, usePlatenFileState, usePlatenFileStateRefresh } from '../hooks/usePlatenSizeContext';
import { PLATEN_SETTINGS_HREF, useCanOpenPlatenSettings } from '../hooks/usePlatenSettingsReach';
import type { ProductUpdateNeed, ProductUpdateRow, ProductUpdatesScanState } from '../hooks/useProductUpdatesScan';
import { isPlatenCellPublished } from '../lib/platen';
import {
  acceptReplaceNotice,
  isReplaceNoticeAccepted,
  skusPrintingExistingFile,
  skusWithMadeSmall,
} from '../lib/replace-notice';
import { STORE_SKU_SETTINGS_HREF, storeSkuProductsHref } from '../lib/store-skus';
import { numericShopifyProductId } from '../lib/tabs';
import { largeWalkIds, owesLargeFile, walkFrom } from '@/features/upload-studio/lib/studio-walk';

/** Rows past this count render through TanStack Virtual (DESIGN.md section 14). */
const VIRTUALIZE_OVER = 200;
/** Set-up rows named before "and N more": the full list is one link away, filtered. */
const SET_UP_VISIBLE = 20;
/** The Products list, filtered to the products that need set-up. */
const SET_UP_PRODUCTS_HREF = storeSkuProductsHref('needed');
/** The Products list, filtered to the products whose store SKU changed since set-up. */
const CHANGED_PRODUCTS_HREF = storeSkuProductsHref('changed');

/** Why a deep link opened nothing, and what the note says. */
const FOCUS_NOTICES = {
  notFound: { testId: 'product-updates-focus-not-found', key: 'productUpdates.focusNotFound' },
  notSetUp: { testId: 'product-updates-focus-not-set-up', key: 'productUpdates.focusNotSetUp' },
  // Listed for set-up with no size waiting for a file here (88a.1): only Set up sets it up.
  setUpNeeded: { testId: 'product-updates-focus-not-set-up', key: 'productUpdates.focusSetUpNeeded' },
  changed: { testId: 'product-updates-focus-changed', key: 'productUpdates.focusChanged' },
} as const;

/** The note for a deep link to a row listed only for its store SKU state. */
function setUpOnlyNotice(row: ProductUpdateRow): keyof typeof FOCUS_NOTICES {
  if (row.setUp?.state === 'changed') return 'changed';
  if (row.setUp?.state === 'needed') return 'setUpNeeded';
  return 'notSetUp';
}

export interface ProductUpdatesTabProps {
  scan: ProductUpdatesScanState;
  artworkTarget: ArtworkUploadTarget;
  admin: boolean;
  /** `?product=`: a Shopify product id (numeric or gid) to scroll to and highlight. */
  focusProductId?: string | null;
  /** Called once the deep link is handled (found, or the walk ended without it). */
  onFocusHandled?: () => void;
}

// ---------------------------------------------------------------------------
// Need grouping: pure, shared by the need lines and the focused view.
// ---------------------------------------------------------------------------

interface NeedGroup {
  key: string;
  assignment: PlatenSizeAssignment;
  needs: ProductUpdateNeed[];
  /** Distinct sizes across the group's needs (the range formatter orders them). */
  sizes: string[];
  /** Distinct SKUs across the group's needs. */
  skuCount: number;
}

/** Needs grouped by platen variant, keeping the server's order (smallest first). */
function groupNeeds(needs: readonly ProductUpdateNeed[]): NeedGroup[] {
  const groups = new Map<string, NeedGroup & { skuSet: Set<string>; sizeSet: Set<string> }>();
  for (const need of needs) {
    let group = groups.get(need.platenVariantKey);
    if (!group) {
      group = {
        key: need.platenVariantKey,
        assignment: { platen: need.platen, platenVariant: need.platenVariant },
        needs: [],
        sizes: [],
        skuCount: 0,
        skuSet: new Set(),
        sizeSet: new Set(),
      };
      groups.set(need.platenVariantKey, group);
    }
    group.needs.push(need);
    for (const sku of need.skus) group.skuSet.add(sku);
    for (const size of need.sizes) group.sizeSet.add(size);
  }
  return [...groups.values()].map(({ skuSet, sizeSet, ...group }) => ({
    ...group,
    sizes: [...sizeSet],
    skuCount: skuSet.size,
  }));
}

/** The shared set order: medium after light and dark (only a BYO product that reads Medium owes it). */
const SET_ORDER = DESIGN_SET_ORDER;

// ---------------------------------------------------------------------------

export function ProductUpdatesTab({ scan, artworkTarget, admin, focusProductId, onFocusHandled }: ProductUpdatesTabProps) {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();
  const { rows, count, hasLoaded, isComplete, isScanning, error, rescan, compositionOrg, declaredMode } = scan;
  const { keptCount, includeKept, setIncludeKept } = scan;
  // Phase 88: a row can owe files, need set-up first (or have a changed store
  // SKU), or both. The files list shows what owes files; each store SKU group
  // shows the rows in its own state, so a changed product is never told to
  // run Set up (Set up skips a changed SKU).
  const fileRows = useMemo(() => rows.filter((row) => row.needs.length > 0), [rows]);
  // Phase 91: the Upload Studio walk is the rows that owe a Large file, in
  // the order on screen (the whole folded list, never a page of it).
  const largeIds = useMemo(() => largeWalkIds(fileRows), [fileRows]);
  // A row whose sizes wait for their file is finished by uploading (one
  // upload sets the whole product up, 88a.1), so it is not told to run Set up.
  const setUpRows = useMemo(
    () => rows.filter((row) => row.setUp?.state === 'needed' && row.setUp.waitingForFile === 0),
    [rows],
  );
  const changedRows = useMemo(() => rows.filter((row) => row.setUp?.state === 'changed'), [rows]);
  const storeSkuRows = setUpRows.length + changedRows.length;
  // A composition org whose switch is not on: its standards are not set up
  // for per-size files at all, so they are never listed (S19).
  const standardsNotSetUp = compositionOrg === true && declaredMode !== 'on';
  // Store SKU mode: every listed product is a store SKU product, so an upload
  // that replaces the file a size prints today warns first (as on Products).
  const storeSkuMode = compositionOrg === true && declaredMode === 'on';
  // The empty state's way to the rules: a link where this org can open
  // Production settings, a plain sentence where its 3PL sets them.
  const canOpenPlatenSettings = useCanOpenPlatenSettings();

  // One open row at a time: the focused view carries the page's one
  // `product-updates-location` picker, and a second copy would duplicate it.
  const [openId, setOpenId] = useState<string | null>(null);
  // Rows that finished in this session, keyed by product, holding the exact
  // row object they finished on. A re-read of the list hands back new row
  // objects, so the chip lasts until the next refresh or tab change and the
  // list never jumps under the cursor meanwhile.
  const [doneRows, setDoneRows] = useState<ReadonlyMap<string, ProductUpdateRow>>(new Map());
  const toggleRefs = useRef(new Map<string, HTMLButtonElement>());

  const isDone = useCallback((row: ProductUpdateRow) => doneRows.get(row.productId) === row, [doneRows]);

  const onAllPublished = useCallback(
    (row: ProductUpdateRow, hadFocus: boolean) => {
      setDoneRows((prev) => new Map(prev).set(row.productId, row));
      setOpenId((current) => (current === row.productId ? null : current));
      // The cached pages still list this product. Mark them stale (no
      // refetch now: nothing observes them) so the next walk re-reads.
      void utils.productUpdates.getPage.invalidate();
      if (hadFocus) {
        requestAnimationFrame(() => toggleRefs.current.get(row.productId)?.focus());
      }
    },
    [utils],
  );

  const listRef = useRef<HTMLDivElement>(null);
  const virtualize = fileRows.length > VIRTUALIZE_OVER;
  const virtualizer = useVirtualizer({
    count: virtualize ? fileRows.length : 0,
    getScrollElement: () => listRef.current,
    estimateSize: () => 76,
    overscan: 8,
    getItemKey: (index) => fileRows[index]?.productId ?? index,
  });

  // --- deep link (?product=) ---------------------------------------------
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const { highlighted: highlightId, highlight } = useOneShotHighlight<string>();
  // The deep-linked row, kept in view while the walk still folds pages above
  // it. Cleared when the walk completes or the operator moves.
  const [anchorId, setAnchorId] = useState<string | null>(null);
  // Focus lands on the row's toggle once, after the first scroll.
  const pendingFocus = useRef<string | null>(null);
  // Why a deep link opened nothing: the product owes no files, it is not set
  // up for per-size files yet, or its store SKU changed since set-up (Phase 88).
  const [focusNotice, setFocusNotice] = useState<keyof typeof FOCUS_NOTICES | null>(null);
  const handledFocus = useRef<string | null>(null);
  const focusTarget = numericShopifyProductId(focusProductId);

  // Wait for the walk: the product may sit on a page not folded yet. Found ->
  // open it, anchor it and tint it. Walk complete without it -> say so.
  // Either way the link is handled once.
  useEffect(() => {
    if (!focusTarget || handledFocus.current === focusTarget) return;
    const row = rows.find((r) => numericShopifyProductId(r.productId) === focusTarget);
    if (row && row.needs.length === 0) {
      // Listed only for its store SKU state: there is nothing to upload yet.
      handledFocus.current = focusTarget;
      setFocusNotice(setUpOnlyNotice(row));
      onFocusHandled?.();
    } else if (row) {
      handledFocus.current = focusTarget;
      setFocusNotice(null);
      setOpenId(row.productId);
      highlight(row.productId);
      pendingFocus.current = row.productId;
      setAnchorId(row.productId);
      onFocusHandled?.();
    } else if (isComplete && !isScanning) {
      handledFocus.current = focusTarget;
      // With the switch off a standard is never listed, so "no missing files"
      // would be a guess; say it is not set up instead.
      setFocusNotice(standardsNotSetUp ? 'notSetUp' : 'notFound');
      onFocusHandled?.();
    }
  }, [focusTarget, rows, isComplete, isScanning, onFocusHandled, highlight, standardsNotSetUp]);

  // Scroll the anchored row just under the sticky bar, and again whenever its
  // position in the list changes (a later page folded rows above it). The row
  // carries the matching scroll margin, so it never lands under the bar.
  const anchorIndex = anchorId ? fileRows.findIndex((r) => r.productId === anchorId) : -1;
  useEffect(() => {
    if (!anchorId || anchorIndex < 0) return;
    if (virtualize) {
      scrollIntoViewBelowActionBar(listRef.current);
      virtualizer.scrollToIndex(anchorIndex, { align: 'start' });
    } else {
      scrollIntoViewBelowActionBar(rowRefs.current.get(anchorId));
    }
    // Keyboard and screen-reader users land on the row too: focus its toggle
    // WITHOUT a second scroll (the smooth scroll above owns the motion). A
    // virtualised row may render a frame later, so try on the next frame.
    const id = pendingFocus.current;
    if (id) {
      requestAnimationFrame(() => {
        const toggle = toggleRefs.current.get(id);
        if (!toggle || pendingFocus.current !== id) return;
        toggle.focus({ preventScroll: true });
        pendingFocus.current = null;
      });
    }
    // `virtualizer` is stable per list; the scroll must not re-run on its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorId, anchorIndex, virtualize]);

  // Stop re-anchoring once the walk is done, or as soon as the operator takes
  // over (never fight a scroll the operator started).
  useEffect(() => {
    if (!anchorId) return undefined;
    if (isComplete && !isScanning) {
      setAnchorId(null);
      return undefined;
    }
    const stop = () => setAnchorId(null);
    const events = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;
    for (const type of events) window.addEventListener(type, stop, { passive: true });
    return () => {
      for (const type of events) window.removeEventListener(type, stop);
    };
  }, [anchorId, isComplete, isScanning]);

  // Always mounted, so the note is announced when it appears.
  const focusNote = (
    <div role="status" aria-live="polite" data-testid="product-updates-focus-status">
      {focusNotice ? (
        <p
          data-testid={FOCUS_NOTICES[focusNotice].testId}
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
        >
          <span>{t(FOCUS_NOTICES[focusNotice].key)}</span>
          <button
            type="button"
            onClick={() => setFocusNotice(null)}
            className="rounded-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            {t('variantArtwork.dismiss')}
          </button>
        </p>
      ) : null}
    </div>
  );

  // BLU-1238 (R3-2): products that keep printing their current files are left
  // out of the walk unless asked for; the note counts them and toggles them.
  const keptNote =
    keptCount > 0 ? (
      <p
        data-testid="product-updates-kept"
        className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
      >
        <span>{t('productUpdates.kept.count', { count: keptCount })}</span>
        <button
          type="button"
          onClick={() => setIncludeKept(!includeKept)}
          disabled={isScanning}
          className="rounded-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:opacity-50"
        >
          {includeKept ? t('productUpdates.kept.hide') : t('productUpdates.kept.show')}
        </button>
      </p>
    ) : null;

  // --- states ------------------------------------------------------------
  const errorBlock = (compact: boolean) => (
    <SectionError
      variant={compact ? 'compact' : 'block'}
      title={t('productUpdates.errorTitle')}
      body={t('productUpdates.errorBody')}
      retryLabel={t('productUpdates.retry')}
      retrying={isScanning}
      onRetry={rescan}
    />
  );

  if (rows.length === 0) {
    if (error) return errorBlock(false);
    // Still walking (or not started): an empty page mid-walk is not an answer.
    if (!hasLoaded || isScanning) return <PageLoader pool="productUpdates" />;
    if (standardsNotSetUp) {
      // Neutral, never the green check: nothing is listed because standards
      // are not set up, not because nothing is missing (S19).
      return (
        <div className="space-y-3">
          {focusNote}
          <div
            className="flex flex-col items-center gap-3 rounded-lg border py-16 text-center"
            data-testid="product-updates-standards-not-set-up"
          >
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-muted text-muted-foreground">
              <Barcode className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold">{t('productUpdates.standardsNotSetUpTitle')}</p>
              <p className="mx-auto max-w-md text-sm text-muted-foreground">{t('productUpdates.standardsNotSetUpBody')}</p>
            </div>
            {admin && canOpenPlatenSettings && (
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link to={STORE_SKU_SETTINGS_HREF}>
                  <Barcode className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('productUpdates.standardsNotSetUpLink')}
                </Link>
              </Button>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {focusNote}
        {keptNote}
        <div className="flex flex-col items-center gap-3 rounded-lg border py-16 text-center">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-100 text-emerald-700">
            <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold">{t('productUpdates.emptyTitle')}</p>
            <p className="mx-auto max-w-md text-sm text-muted-foreground">{t('productUpdates.emptyBody')}</p>
          </div>
          {canOpenPlatenSettings ? (
            <Button asChild variant="outline" size="sm" className="gap-1.5">
              <Link to={PLATEN_SETTINGS_HREF}>
                <Frame className="h-3.5 w-3.5" aria-hidden="true" />
                {t('productUpdates.emptyCta')}
              </Link>
            </Button>
          ) : (
            <p className="mx-auto max-w-md text-sm text-muted-foreground">{t('platen.rulesSetBy3pl')}</p>
          )}
        </div>
      </div>
    );
  }

  const renderRow = (row: ProductUpdateRow) => (
    <ProductUpdateListRow
      key={row.productId}
      row={row}
      open={openId === row.productId}
      done={isDone(row)}
      highlighted={highlightId === row.productId}
      rowRef={(el) => {
        if (el) rowRefs.current.set(row.productId, el);
        else rowRefs.current.delete(row.productId);
      }}
      onOpenChange={(open) => setOpenId(open ? row.productId : null)}
      toggleRef={(el) => {
        if (el) toggleRefs.current.set(row.productId, el);
        else toggleRefs.current.delete(row.productId);
      }}
      artworkTarget={artworkTarget}
      admin={admin}
      storeSkuMode={storeSkuMode}
      onAllPublished={onAllPublished}
      onStale={rescan}
      largeIds={largeIds}
    />
  );

  // The header's counts: each kind of work apart, each only when above 0.
  const headerCounts = [
    fileRows.length > 0 ? t('productUpdates.filesNeeded', { count: fileRows.length }) : null,
    setUpRows.length > 0 ? t('productUpdates.setUpNeeded', { count: setUpRows.length }) : null,
    changedRows.length > 0 ? t('productUpdates.changedCount', { count: changedRows.length }) : null,
  ].filter((part): part is string => part !== null);

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">{t('productUpdates.intro')}</p>

      {focusNote}

      {keptNote}

      {error && errorBlock(true)}

      <div className="overflow-hidden rounded-lg border">
        <div
          data-testid="product-updates-header"
          className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2"
        >
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('productUpdates.listHeader')}
          </span>
          {storeSkuRows > 0 ? (
            // More than one kind of work, counted apart (each only when above 0).
            <span className="flex flex-wrap items-center gap-x-1.5 text-[11px] font-semibold tabular-nums text-foreground">
              {headerCounts.map((part, index) => (
                <span key={part} className="contents">
                  {index > 0 && <span aria-hidden="true" className="text-muted-foreground">·</span>}
                  <span>{part}</span>
                </span>
              ))}
            </span>
          ) : (
            <span className="text-[11px] font-semibold tabular-nums text-foreground">{count}</span>
          )}
          {isScanning && (
            <Loader2
              data-testid="product-updates-spinner"
              className="ml-auto h-3.5 w-3.5 animate-spin text-muted-foreground motion-reduce:animate-none"
              aria-hidden="true"
            />
          )}
          {/* The walk is progressive: say so to assistive tech once, politely. */}
          <span className="sr-only" aria-live="polite">
            {isComplete
              ? storeSkuRows > 0
                ? headerCounts.join(t('productUpdates.need.joiner'))
                : t('productUpdates.listComplete', { count })
              : ''}
          </span>
        </div>

        {setUpRows.length > 0 && <StoreSkuGroup kind="needed" rows={setUpRows} />}
        {changedRows.length > 0 && <StoreSkuGroup kind="changed" rows={changedRows} />}

        {virtualize ? (
          <div ref={listRef} className="max-h-[70vh] overflow-y-auto" data-testid="product-updates-virtual-list">
            <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
              {virtualizer.getVirtualItems().map((item) => {
                const row = fileRows[item.index]!;
                return (
                  <div
                    key={row.productId}
                    ref={virtualizer.measureElement}
                    data-index={item.index}
                    className="absolute left-0 top-0 w-full border-b"
                    style={{ transform: `translateY(${item.start}px)` }}
                  >
                    {renderRow(row)}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="divide-y">{fileRows.map(renderRow)}</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 88: products whose store SKUs need set-up, or changed since set-up,
// before (more of) their sizes can get files.
// ---------------------------------------------------------------------------

/** Each store SKU group's copy, link and test id, by the rows' own state. */
const STORE_SKU_GROUPS = {
  needed: {
    testId: 'product-updates-set-up-group',
    rowTestId: 'product-update-set-up-row',
    heading: 'productUpdates.setUpGroupHeading',
    body: 'productUpdates.setUpGroupBody',
    link: 'productUpdates.setUpGroupLink',
    href: SET_UP_PRODUCTS_HREF,
  },
  changed: {
    testId: 'product-updates-changed-group',
    rowTestId: 'product-update-changed-row',
    heading: 'productUpdates.changedGroupHeading',
    body: 'productUpdates.changedGroupBody',
    link: 'productUpdates.changedGroupLink',
    href: CHANGED_PRODUCTS_HREF,
  },
} as const;

/**
 * One group, one heading, one next step, one link to the Products list
 * filtered to the same state (where the Set up button lives for `needed`; the
 * changed products to check in the store for `changed`). No per-row action:
 * grouped and per-row Set up come later. The first rows are named so the
 * operator recognises them; the rest are counted, never silently cut.
 */
function StoreSkuGroup({ kind, rows }: { kind: 'needed' | 'changed'; rows: ProductUpdateRow[] }) {
  const { t } = useTranslation('productSync');
  const headingId = useId();
  const copy = STORE_SKU_GROUPS[kind];
  const shown = rows.slice(0, SET_UP_VISIBLE);
  const more = rows.length - shown.length;
  return (
    <section aria-labelledby={headingId} className="border-b bg-muted/20 px-3 py-3" data-testid={copy.testId}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <h3 id={headingId} className="flex items-center gap-1.5 text-sm font-semibold">
            <Barcode className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            {t(copy.heading)}
          </h3>
          <p className="text-xs text-muted-foreground">{t(copy.body)}</p>
        </div>
        <Button asChild variant="outline" size="sm" className="h-8 shrink-0 gap-1.5 px-3 text-xs">
          <Link to={copy.href}>
            {t(copy.link)}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </Button>
      </div>
      <ul className="mt-2 divide-y rounded-md border bg-background">
        {shown.map((row) => (
          <li key={row.productId} className="flex min-w-0 items-center gap-2 px-3 py-2" data-testid={copy.rowTestId}>
            <span className="truncate text-sm font-medium" title={row.title}>{row.title}</span>
            <span className="shrink-0 rounded-md bg-background px-1.5 py-0.5 font-mono text-[11px] ring-1 ring-border">
              {row.productTypeCode}
            </span>
            <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
              {kind === 'changed'
                ? t('productUpdates.changedVariants', { count: row.setUp?.changed ?? 0 })
                : t('productUpdates.setUpVariants', { count: row.setUp?.variants ?? 0 })}
            </span>
          </li>
        ))}
      </ul>
      {more > 0 && <p className="mt-1.5 text-xs text-muted-foreground">{t('productUpdates.setUpMore', { count: more })}</p>}
    </section>
  );
}

// ---------------------------------------------------------------------------
// One row: title, product type chip, need lines (or the done chip), toggle.
// ---------------------------------------------------------------------------

interface ProductUpdateListRowProps {
  row: ProductUpdateRow;
  open: boolean;
  done: boolean;
  /** The deep-linked row, for the one-shot highlight. */
  highlighted: boolean;
  rowRef: (el: HTMLDivElement | null) => void;
  onOpenChange: (open: boolean) => void;
  toggleRef: (el: HTMLButtonElement | null) => void;
  artworkTarget: ArtworkUploadTarget;
  admin: boolean;
  /** Store SKU mode: an upload replacing a file a size prints today warns first. */
  storeSkuMode: boolean;
  onAllPublished: (row: ProductUpdateRow, hadFocus: boolean) => void;
  onStale: () => void;
  /** Phase 91: the ids the Upload Studio walks, in screen order (rows owing a Large file). */
  largeIds: readonly string[];
}

function ProductUpdateListRow({
  row,
  open,
  done,
  highlighted,
  rowRef,
  onOpenChange,
  toggleRef,
  artworkTarget,
  admin,
  storeSkuMode,
  onAllPublished,
  onStale,
  largeIds,
}: ProductUpdateListRowProps) {
  const { t } = useTranslation('productSync');
  const contentId = useId();
  const groups = useMemo(() => groupNeeds(row.needs), [row.needs]);
  const waitingForFile = row.setUp?.state === 'needed' ? row.setUp.waitingForFile : 0;

  const groupName = (group: NeedGroup): string => platenGroupLabel(t, group.assignment);

  // One line per platen variant: its sets in light, dark, medium, single order, each
  // naming its missing sides, front and back merged.
  const needLine = (group: NeedGroup): string => {
    const parts: string[] = [];
    for (const set of SET_ORDER) {
      const sides = new Set(group.needs.filter((n) => n.set === set).map((n) => n.side));
      if (sides.size === 0) continue;
      const sidesText =
        sides.size === 2
          ? t('productUpdates.need.both')
          : sides.has('front')
            ? t('productUpdates.need.front')
            : t('productUpdates.need.back');
      if (set === 'single') {
        parts.push(sidesText);
      } else {
        const setText =
          set === 'light'
            ? t('productUpdates.need.setLight')
            : set === 'medium'
              ? t('productUpdates.need.setMedium')
              : t('productUpdates.need.setDark');
        parts.push(t('productUpdates.need.withSet', { set: setText, sides: sidesText }));
      }
    }
    return t('productUpdates.need.line', {
      name: groupName(group),
      parts: parts.join(t('productUpdates.need.joiner')),
    });
  };

  // A non-admin can look but not upload: "Details", never a promise of files.
  const toggleLabel = open ? t('productUpdates.close') : admin ? t('productUpdates.uploadFiles') : t('productUpdates.details');

  return (
    <Collapsible
      ref={rowRef}
      open={open}
      onOpenChange={onOpenChange}
      data-product-row=""
      data-highlighted={highlighted ? '' : undefined}
      className={cn(
        // Lands under the top header and the sticky action bar, not beneath them.
        BELOW_ACTION_BAR_SCROLL_MARGIN,
        // The one-shot tint: background only, so it never fights the focus
        // ring and the list's overflow clip cannot cut it (DESIGN.md section 10).
        ONE_SHOT_HIGHLIGHT_TRANSITION,
        highlighted && ONE_SHOT_HIGHLIGHT_CLASS,
      )}
    >
      <div className="flex items-start gap-3 px-3 py-3 hover:bg-muted/40 motion-reduce:hover:bg-muted/40">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-w-0 items-center gap-2">
            <p data-testid="product-update-title" className="truncate text-sm font-semibold" title={row.title}>
              {row.title}
            </p>
            <span className="shrink-0 rounded-md bg-background px-1.5 py-0.5 font-mono text-[11px] ring-1 ring-border">
              {row.productTypeCode}
            </span>
            {row.keepsCurrentFiles && (
              <span
                data-testid="product-update-kept-badge"
                className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground ring-1 ring-border"
              >
                {t('productUpdates.kept.badge')}
              </span>
            )}
          </div>
          {done ? (
            <p role="status">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-300/80">
                <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                {t('productUpdates.done')}
              </span>
            </p>
          ) : (
            <>
              <ul className="space-y-0.5">
                {groups.map((group) => (
                  <li key={group.key} className="text-xs text-amber-800">
                    {needLine(group)}
                  </li>
                ))}
              </ul>
              {row.keepsCurrentFiles && (
                <p className="text-xs text-muted-foreground">{t('productUpdates.kept.rowNote')}</p>
              )}
              {/* 88a.1 (D4): an upload here sets this product up. */}
              {admin && waitingForFile > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('productUpdates.waitingForFile', { count: waitingForFile })}
                </p>
              )}
            </>
          )}
        </div>
        {!done && owesLargeFile(row) && <PlaceInStudioLink productId={row.productId} largeIds={largeIds} />}
        <CollapsibleTrigger asChild>
          <Button
            ref={toggleRef}
            variant="outline"
            size="sm"
            aria-controls={contentId}
            className="h-8 shrink-0 px-3 text-xs"
          >
            {toggleLabel}
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent
        id={contentId}
        role="region"
        aria-label={t('productUpdates.regionAria', { title: row.title })}
        className="border-t bg-muted/30 p-4 pb-8"
      >
        <FocusedView
          row={row}
          groups={groups}
          groupName={groupName}
          done={done}
          artworkTarget={artworkTarget}
          admin={admin}
          storeSkuMode={storeSkuMode}
          onAllPublished={onAllPublished}
          onStale={onStale}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Phase 91: a row's way into the Upload Studio. Router state carries the
 * list as folded on screen (`walk`, at most 500 ids) and where it was opened
 * from, so Save and next walks the list and the last row comes back here
 * (the shared `StudioEntryLink`, which also keeps the page for Back).
 */
function PlaceInStudioLink({ productId, largeIds }: { productId: string; largeIds: readonly string[] }) {
  const { t } = useTranslation('uploadStudio');
  return (
    <StudioEntryLink
      productId={productId}
      label={t('entry.place')}
      walk={walkFrom(largeIds, productId)}
      data-testid="product-update-place"
    />
  );
}

// ---------------------------------------------------------------------------
// The focused view: only the missing slots, grouped by platen variant.
// ---------------------------------------------------------------------------

interface FocusedViewProps {
  row: ProductUpdateRow;
  groups: NeedGroup[];
  groupName: (group: NeedGroup) => string;
  done: boolean;
  artworkTarget: ArtworkUploadTarget;
  admin: boolean;
  storeSkuMode: boolean;
  onAllPublished: (row: ProductUpdateRow, hadFocus: boolean) => void;
  onStale: () => void;
}

function FocusedView({
  row,
  groups,
  groupName,
  done,
  artworkTarget,
  admin,
  storeSkuMode,
  onAllPublished,
  onStale,
}: FocusedViewProps) {
  const { t } = useTranslation('productSync');
  const rootRef = useRef<HTMLDivElement>(null);

  // Every SKU this product owes a file for, read through the same stable
  // 30-SKU chunks the colour grid uses, so the chunks are shared and the
  // done state is the server's marker truth, never a local guess.
  const skus = useMemo(() => {
    const set = new Set<string>();
    for (const need of row.needs) for (const sku of need.skus) set.add(sku);
    return [...set];
  }, [row.needs]);
  // Round 4 (86-34 review): a Small slot asks which sides were made from
  // Large, so an own file dropped on one is warned first, as on the Products tab.
  const anySmall = row.needs.some((need) => need.platenVariant === 'Small');
  const fileState = usePlatenFileState(skus, true, { includeMadeFromLarge: anySmall });

  const { invalidateFileState, onPlatenCommitFailed: refreshAfterRefusedCommit } = usePlatenFileStateRefresh(skus);

  // A refused commit means the rules changed under the list: re-read the
  // rules, this product's file state and the worklist, and say why
  // (persistent, DESIGN.md section 15.5).
  const onPlatenCommitFailed = useCallback(
    (message: string) => {
      refreshAfterRefusedCommit(message);
      onStale();
    },
    [refreshAfterRefusedCommit, onStale],
  );

  const fileStateReady = !fileState.isLoading && !fileState.isError;
  const allPublished =
    fileStateReady &&
    row.needs.every((need) =>
      isPlatenCellPublished(need.skus, need.side, { platen: need.platen, platenVariant: need.platenVariant }, fileState.bySku),
    );

  // Report once, when the last slot lands. A row already marked done can be
  // reopened to look at its files without closing itself again.
  useEffect(() => {
    if (!allPublished || done) return;
    const root = rootRef.current;
    const hadFocus = !!root && (root.contains(document.activeElement) || document.activeElement === document.body);
    onAllPublished(row, hadFocus);
  }, [allPublished, done, onAllPublished, row]);

  // The count the cell shows as published: SKUs whose side is covered by an
  // exact marker on this platen variant (the shared test, D-22).
  const coveredCount = (need: ProductUpdateNeed): number =>
    need.skus.filter((sku) =>
      isPlatenSideCovered({ platen: need.platen, platenVariant: need.platenVariant }, fileState.bySku[sku]?.[need.side] ?? null),
    ).length;

  const setLabel = (set: DesignSet): string =>
    set === 'light'
      ? t('tier2.tagLight')
      : set === 'dark'
        ? t('tier2.tagDark')
        : set === 'medium'
          ? t('tier2.tagMedium')
          : t('colourArtwork.allGarments');

  // D-12 (S20), as on the Products tab: the first upload into a store SKU
  // that prints from an existing file today warns once per product, inside
  // the pixel check's one dialog. Accepting it on either tab covers both. A
  // need's sizes are not one per SKU, so the warning names them only when
  // every SKU in the cell prints an existing file; otherwise it counts them.
  const { productId } = row;
  const fileStateBySku = fileState.bySku;
  const replaceNoticeFor = useCallback(
    (need: ProductUpdateNeed): PlatenReplaceNotice | null => {
      if (isReplaceNoticeAccepted(productId)) return null;
      const assignment = { platen: need.platen, platenVariant: need.platenVariant };
      const printing = skusPrintingExistingFile(need.skus, need.side, assignment, fileStateBySku);
      if (printing.length === 0) return null;
      let body: string;
      if (printing.length < need.skus.length) {
        body = t('productUpdates.replaceWarningBodySome', { count: printing.length });
      } else {
        const sizes = formatPlatenSizeRange(t, need.sizes);
        body =
          need.set === 'single'
            ? t('colourArtwork.replaceWarningBodyAllGarments', { sizes })
            : t('colourArtwork.replaceWarningBody', {
                sizes,
                colour:
                  need.set === 'light' ? t('tier2.light') : need.set === 'medium' ? t('tier2.medium') : t('tier2.dark'),
              });
      }
      return { title: t('colourArtwork.replaceWarningTitle'), body, accept: () => acceptReplaceNotice(productId) };
    },
    [fileStateBySku, productId, t],
  );
  // R4-4: before an own file replaces a Small made from Large, the slot says
  // it is kept from then on (every time; nothing to accept for good).
  const madeReplaceNotice = (need: ProductUpdateNeed): PlatenReplaceNotice | null =>
    skusWithMadeSmall(need.skus, need.side, fileStateBySku).length > 0
      ? { title: t('platen.derive.replaceTitle'), body: t('platen.derive.replaceBody'), accept: () => undefined }
      : null;
  const sideLabel = (side: 'front' | 'back'): string =>
    side === 'front' ? t('productUpdates.sideFront') : t('productUpdates.sideBack');
  // The set-up toast names the product (88a.1, D4).
  const cellProduct = { id: productId, title: row.title };

  // ONE pixel-size check for the whole view, so only one dialog is ever open.
  // Round 4 (R4-5): the org's Large numbers, from the org platen read the
  // page already holds (never refetched here). This view never makes a Small
  // from Large: its Small cells stay plain upload cells.
  const orgPixels = useOrgPlatenContext({ refetchOnMount: false }).data?.pixels ?? null;
  const pixelCheck = usePlatenPixelCheck(orgPixels);
  const anyBack = row.needs.some((need) => need.side === 'back');

  const renderReady = (locationId: string): ReactNode => {
    if (fileState.isLoading) {
      // Pending is not "missing" (DESIGN.md section 15.1).
      return (
        <div className="space-y-2 rounded-lg border bg-background p-3" role="status" aria-busy="true">
          <div className="h-4 w-40 animate-pulse rounded bg-muted motion-reduce:animate-none" />
          <div className="grid gap-3 md:grid-cols-2">
            <div className="h-24 animate-pulse rounded bg-muted motion-reduce:animate-none" />
            <div className="h-24 animate-pulse rounded bg-muted motion-reduce:animate-none" />
          </div>
        </div>
      );
    }
    if (fileState.isError) {
      // Failed is not "missing" either: painting empty slots from a failed read
      // invites a re-upload over files that are already there.
      return (
        <SectionError
          variant="compact"
          body={t('productUpdates.fileStateError')}
          retryLabel={t('productUpdates.retry')}
          onRetry={invalidateFileState}
        />
      );
    }
    return (
      <div className="space-y-3">
        {groups.map((group) => {
          const name = groupName(group);
          return (
            <section key={group.key} data-testid="platen-group" className="rounded-lg border bg-background" aria-label={name}>
              <header className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b bg-muted/40 px-3 py-3">
                <span data-testid="platen-group-name" className="text-xs font-semibold">
                  {name}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {t('platen.groupRange', { range: formatPlatenSizeRange(t, group.sizes) })}
                </span>
                <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
                  {t('platen.skuCount', { count: group.skuCount })}
                </span>
              </header>
              <div className="grid gap-3 p-3 md:grid-cols-2">
                {group.needs.map((need) => {
                  const set = setLabel(need.set);
                  return (
                    <div key={`${need.set}:${need.side}`} className="min-w-0 space-y-1.5">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                        {t('productUpdates.cellTitle', { set, side: sideLabel(need.side) })}
                      </p>
                      <ColourArtworkCell
                        side={need.side}
                        colourLabel={t('platen.cellLabel', { colour: set, group: name })}
                        skus={need.skus}
                        locationId={locationId}
                        admin={admin}
                        dark={need.set === 'dark'}
                        restingPublishedCount={0}
                        restingUrl={null}
                        publishedOverride={coveredCount(need)}
                        onPublishStateChange={invalidateFileState}
                        expectedPlatenVariantKey={need.platenVariantKey}
                        platen={need.platen}
                        pixelGuard={pixelCheck.guard}
                        filePixels={expectedPlatenFilePixels(orgPixels, need.platen)}
                        replaceNotice={() =>
                          (storeSkuMode ? replaceNoticeFor(need) : null) ?? madeReplaceNotice(need)
                        }
                        onPlatenCommitFailed={onPlatenCommitFailed}
                        showBackNote={false}
                        product={cellProduct}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
        {anyBack && <p className="text-[11px] text-muted-foreground">{t('variantArtwork.backSideNote')}</p>}
      </div>
    );
  };

  return (
    <div ref={rootRef} className="space-y-4">
      {admin ? (
        <ArtworkTargetPicker target={artworkTarget} id="product-updates-location" />
      ) : (
        // Uploads need an admin: say so once, instead of a picker that leads
        // nowhere and empty slots that read as broken.
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="product-updates-admin-only">
          <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t('productUpdates.adminOnly')}
        </p>
      )}
      {admin ? (
        // Same order and copy as colour mode: "still reading your locations"
        // is never shown as "you have no locations".
        <ArtworkLocationGate
          isLoading={artworkTarget.isLoading}
          needsChoice={artworkTarget.needsChoice}
          locationId={artworkTarget.location?.id ?? null}
          skeletonRows={1}
        >
          {renderReady}
        </ArtworkLocationGate>
      ) : (
        // Read-only slots never upload, so they need no target location.
        renderReady(artworkTarget.location?.id ?? '')
      )}
      {pixelCheck.dialog}
    </div>
  );
}
